import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ScheduleRequest } from '@dr-customer-offers-ui/lib-interfaces';
import { Store } from '@ngrx/store';
import {
  BulkType,
  DataModel,
  NgxIntervalDataGridRowModel,
  NgxIntervalDataGridService,
  TableDataTypes,
  Week,
  WeekDays
} from '@ngx-interval-data-grid';
import moment from 'moment';
import { filter, Observable } from 'rxjs';
import { BulkInput, InputTypeEnum, OffersDataInterface, TabOption } from '../../../shared/models';
import { BusinessRole } from '../../../shared/models/role';
import { UIState } from '../../../shared/models/utility';
import { DataModelService } from '../../../shared/services/data-model.service';
import { DataViewModelService } from '../../../shared/services/data-vm.service';
import { InternalService } from '../../../shared/services/internal.service';
import { MixPanelService } from '../../../shared/services/mixpanel.service';
import { AppState } from '../../../store';
import * as Actions from '../../../store/actions/com.actions';
import * as Selectors from '../../../store/selectors/com.selectors';
import { FiltersStateService } from '../../filters/filters-state.service';

@Component({
  selector: 'dr-customer-offers-ui-schedule-table',
  templateUrl: './schedule-tab-table.component.html',
  styleUrls: ['./schedule-tab-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('150ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScheduleTabTableComponent implements OnInit {
  protected scheduledOffersLoading = this.store.selectSignal(Selectors.selectScheduledOffersLoading);
  protected currentRegViewConfig = this.store.selectSignal(Selectors.selectCurrentRegViewConfig);

  UIState = UIState;
  protected scheduleData!: NgxIntervalDataGridRowModel[] | null;
  public timezoneAbbr!: string;

  // Define column headers and fields
  columns: string[] = WeekDays;
  displayedColumns: string[] = ['offer', 'time_period', ...this.columns];
  serverData: NgxIntervalDataGridRowModel[] = [];
  public dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);
  protected minDate!: moment.Moment;
  protected maxDate!: moment.Moment;
  protected editModeSignal: WritableSignal<boolean> = signal(false);
  protected disableConfirmButton$: Observable<boolean> = this.ngxService.getDisableSave();
  protected applyButtonDisabled = this.filtersStateService.isApplyButtonDisabledSignal;
  protected displaySelectedReg = '';
  protected dates: number[] = [];
  private userHasIrishMarketUserRole = this.store.selectSignal(Selectors.selectHasBusinessRole(BusinessRole.IrishMarketUser));
  private userHasCustomerOfferUserRole = this.store.selectSignal(Selectors.selectHasBusinessRole(BusinessRole.CustomerOfferUser));
  protected showEditButton = computed(() => {
    if (!this.dataVMService.externalUserSignal()) return true;
    return this.userHasIrishMarketUserRole() && this.userHasCustomerOfferUserRole();
  });
  protected isExternalUser = computed(() => !!this.dataVMService.externalUserSignal());

  constructor(
    private ngxService: NgxIntervalDataGridService,
    private dataModelService: DataModelService,
    private dataVMService: DataViewModelService,
    private internalService: InternalService,
    private mixPanelService: MixPanelService,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    private destroyRef: DestroyRef,
    private store: Store<AppState>,
    private filtersStateService: FiltersStateService
  ) {}

  ngOnInit(): void {
    this.store
      .select(Selectors.selectCurrentScheduledOffers)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((offers) => offers !== null)
      )
      .subscribe((offers: OffersDataInterface | null) => {
        if (offers) this.processGroupedData(offers);
        this.filtersStateService.setApplyButtonChanging(this.dataVMService.selectedRegFormControl.value);
      });

    this.loadData();

    // ::TODO:: Can remove it later if not needed. Commented for now to remove duplicate calls.
    // this.router.events
    //   .pipe(
    //     takeUntilDestroyed(this.destroyRef),
    //     filter((event) => event instanceof NavigationEnd)
    //   )
    //   .subscribe(() => {
    //     this.loadData();
    //   });

    this.mixPanelService.viewTab('Schedule');
    this.applyBulk();
  }

  get selectedTabSignal(): WritableSignal<TabOption> {
    return this.dataVMService.selectedTabOptionSignal;
  }

  triggerEditMode(isEditMode: boolean): void {
    this.editModeSignal.set(isEditMode);
  }

  private loadData(): void {
    setTimeout(() => {
      this.store.dispatch(Actions.loadScheduledOffers());
    }, 10);
    this.displaySelectedReg = this.currentRegViewConfig()?.display_labels || '';
  }

  /**
   * This method is updated each time you enter a value on any of the input field
   * @param element provides the whole element which is being updated
   * @param event is an KeyboardEvent where we get the value entered.
   * @param week is used to provide the week which we are updating the value for.
   */
  inputToCellValue(element: NgxIntervalDataGridRowModel, inputEvent: KeyboardEvent | undefined, week: string) {
    const target = inputEvent?.target as HTMLInputElement | null;
    const value: number | null = target?.valueAsNumber === undefined || isNaN(target.valueAsNumber) ? null : target?.valueAsNumber;
    const oldData: NgxIntervalDataGridRowModel = this.ngxService
      .getUntouchedDataFromServer()
      .tableNgxIntervalData[TableDataTypes.SCHEDULE].find((f) => f.timePeriodKey === element.timePeriodKey) as NgxIntervalDataGridRowModel;
    const nullReset = oldData[week as Week].value === null && value === null;
    const isValid =
      nullReset ||
      (value !== null && value <= (this.currentRegViewConfig()?.maximumValue ?? 0) && value >= (this.currentRegViewConfig()?.minimumValue ?? 0));

    const updatedDataElement = this.ngxService.updateElementValue({
      weekName: week as Week,
      timePeriodKey: element.timePeriodKey,
      value,
      invalid_value: !isValid,
      tableDataType: TableDataTypes.SCHEDULE
    });
    this.serverData = [...updatedDataElement];
  }

  inputToCellOfferLocked(element: NgxIntervalDataGridRowModel, isChecked: boolean, week: string): void {
    // Defensive guard: the checkbox is not rendered for external users.
    if (this.isExternalUser()) return;

    const updatedDataElement = this.ngxService.updateElementOfferLocked({
      weekName: week as Week,
      timePeriodKey: element.timePeriodKey,
      offer_lock: isChecked,
      tableDataType: TableDataTypes.SCHEDULE
    });
    this.serverData = [...updatedDataElement];
  }

  inputToCellOptOut(element: NgxIntervalDataGridRowModel, isChecked: boolean, week: string) {
    const updatedDataElement = this.ngxService.updateElementOptOut({
      weekName: week as Week,
      timePeriodKey: element.timePeriodKey,
      opt_out: isChecked,
      tableDataType: TableDataTypes.SCHEDULE
    });
    this.serverData = [...updatedDataElement];
  }

  /**
   * This method is used when user cancels from edit mode. Here we forget all his previous updates and take data we recieved from the server.
   */
  cancel() {
    const data: DataModel[] = this.ngxService.getUntouchedDataFromServer().dataModelRef[TableDataTypes.SCHEDULE] as DataModel[];
    this.ngxService.clearUnSavedData();
    this.editModeSignal.set(false);
    this.dataSource.data = this.ngxService.groupByDayOfWeek(data, this.currentRegViewConfig()?.timeZone ?? 'UTC', TableDataTypes.SCHEDULE);
  }

  /**
   * This is the main method used to save the updated data and switch back to the readonly mode
   */

  save() {
    const scheduleToPost: ScheduleRequest[] = this.dataModelService.getScheduleToPost(
      this.ngxService.unsavedData,
      this.scheduleData,
      this.currentRegViewConfig()
    );
    this.postSchedule(scheduleToPost);
  }

  // Check if NgxIntervalDataGridRowModel[] had value before. Return true is it has any value and false is everything is null
  private hasAnyValue(): boolean {
    if (!this.scheduleData) {
      return false;
    }
    return this.scheduleData.some(
      (row: NgxIntervalDataGridRowModel) =>
        row.Monday.value !== null ||
        row.Tuesday.value !== null ||
        row.Wednesday.value !== null ||
        row.Thursday.value !== null ||
        row.Friday.value !== null ||
        row.Saturday.value !== null ||
        row.Sunday.value !== null
    );
  }

  postSchedule(schedule: ScheduleRequest[]) {
    !this.hasAnyValue()
      ? this.store.dispatch(
          Actions.postScheduledOffers({
            offers: schedule
          })
        )
      : this.store.dispatch(
          Actions.putScheduledOffers({
            offers: schedule
          })
        );
  }

  applyBulkOptout(day: string, isChecked: boolean): void {
    this.serverData = [...this.ngxService.updateOptOutBulk(BulkType.DAY, isChecked, TableDataTypes.SCHEDULE, day as Week)];
    this.dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);
  }

  applyBulk(): void {
    this.internalService.getBulkInputs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((b: BulkInput | null) => {
      if (!b) return;
      switch (b.bulkType) {
        case BulkType.CUSTOM:
          {
            if (b.inputType === InputTypeEnum.VALUE && b.value !== null && b.value !== undefined) {
              this.serverData = [
                ...this.ngxService.updateValueBulk(
                  BulkType.CUSTOM,
                  b.value,
                  TableDataTypes.SCHEDULE,
                  undefined,
                  moment(b.startDateAndTime),
                  moment(b.endDateAndTime),
                  false,
                  b.operation
                )
              ];
            } else if (b.opt_out) {
              this.serverData = [
                ...this.ngxService.updateOptOutBulk(
                  BulkType.CUSTOM,
                  b.opt_out,
                  TableDataTypes.SCHEDULE,
                  undefined,
                  moment(b.startDateAndTime),
                  moment(b.endDateAndTime),
                  b.operation
                )
              ];
            } else if (b.offer_lock) {
              this.serverData = [
                ...this.ngxService.updateOfferLockBulk(
                  b.offer_lock,
                  TableDataTypes.SCHEDULE,
                  undefined,
                  moment(b.startDateAndTime),
                  moment(b.endDateAndTime),
                  b.operation
                )
              ];
            }
          }
          break;
        case BulkType.DAY:
          if (b.value !== null && b.value !== undefined)
            this.serverData = [...this.ngxService.updateValueBulk(BulkType.DAY, b.value, TableDataTypes.SCHEDULE, b.week)];
          break;
        case BulkType.WEEK:
          if (b.value !== null && b.value !== undefined)
            this.serverData = [...this.ngxService.updateValueBulk(BulkType.WEEK, b.value, TableDataTypes.SCHEDULE)];
          break;
      }
      this.dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);
      this.cdr.detectChanges();
    });
  }

  private processGroupedData(value: OffersDataInterface): void {
    this.timezoneAbbr = moment.tz(this.currentRegViewConfig()?.timeZone ?? 'UTC').zoneName();
    this.dataSource.data = this.scheduleData = value.values ?? [];
    this.displayedColumns = value.values?.[0].groupedOffersIntervals ? ['time_period', ...this.columns] : ['offer', 'time_period', ...this.columns];

    this.editModeSignal.set(false);
    this.cdr.detectChanges();
  }
}
